import React, { useState, useEffect } from 'react';
import {
  Card, Table, Button, Form, Row, Col, Badge, Modal,
  Spinner, OverlayTrigger, Tooltip, Pagination
} from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';

const DataPasien = () => {
  const navigate = useNavigate();

  // Helper IP API
  const getApiUrl = () => {
    return localStorage.getItem('api_url') || 'http://localhost:8000';
  };

  // State Data & Loading
  const [pasienList, setPasienList] = useState([]);
  const [isLoading, setIsLoading]   = useState(true);
  const [fetchError, setFetchError] = useState(null);

  // State Clock
  const [time, setTime] = useState({ date: '', clock: '' });

  // State Filter & Search Parameter (no_medrec dan nama ke backend /show_pasien)
  const [inputNoMedrec, setInputNoMedrec] = useState('');
  const [inputNama, setInputNama]         = useState('');
  const [filterGender, setFilterGender]   = useState(''); // '', 'Laki-Laki', 'Perempuan'
  const [currentPage, setCurrentPage]     = useState(1);
  const [itemsPerPage]                   = useState(10);

  // State Modal Detail Pasien
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedPasien, setSelectedPasien]   = useState(null);

  // State Modal Preview Kartu Pasien (/buat_kartu_pasien)
  const [showKartuModal, setShowKartuModal]         = useState(false);
  const [selectedKartuData, setSelectedKartuData] = useState(null);
  const [isGeneratingKartu, setIsGeneratingKartu] = useState(false);
  const [kartuError, setKartuError]               = useState(null);

  // Mock Initial Data Pasien (/show_pasien)
  const mockPasienData = [
    {
      "agama": "Lainnya",
      "id": "Lainnya",
      "jln1": "DUSUN CINYOSOG",
      "kecamatan": "CILEUNGSI",
      "kelamin": "Perempuan",
      "komplek1": "-",
      "kota": "BOGOR",
      "nama": "A SALIM",
      "nama_panggil": "A",
      "no_ktp": "3201071909600005",
      "no_medrec": "339727",
      "pekerjaan": "0001830414431",
      "perusahaan": "MANDIRI K1",
      "rt": "03",
      "rw": "02",
      "status": "Lainnya",
      "sts": "K",
      "tempat": "TEGAL",
      "tgllahir": "19/09/1960"
    },
    {
      "agama": "Lainnya",
      "id": "Lainnya",
      "jln1": "CLUSTER MODESTA BLOK K10",
      "kecamatan": "KLAPANUNGGAL",
      "kelamin": "Perempuan",
      "komplek1": "PERUM COCO GARDEN",
      "kota": "BOGOR",
      "nama": "AAFIYAH NADHIFA AL AWALIA H",
      "nama_panggil": "AAFIYAH",
      "no_ktp": "-",
      "no_medrec": "169052",
      "pekerjaan": "0001962093273",
      "perusahaan": "TNI AL",
      "rt": "01",
      "rw": "14",
      "status": "Lainnya",
      "sts": "T",
      "tempat": "BANDUNG",
      "tgllahir": "16/12/2012"
    },
    {
      "agama": "Islam",
      "id": "Islam",
      "jln1": "JL. RAYA NAROGONG KM 18",
      "kecamatan": "GUNUNG PUTRI",
      "kelamin": "Laki - Laki",
      "komplek1": "GRIYA UTAMA",
      "kota": "BOGOR",
      "nama": "BUDI SANTOSO",
      "nama_panggil": "BUDI",
      "no_ktp": "3201081504850002",
      "no_medrec": "254109",
      "pekerjaan": "KARYAWAN SWASTA",
      "perusahaan": "BPJS KESEHATAN",
      "rt": "05",
      "rw": "08",
      "status": "Kawin",
      "sts": "K",
      "tempat": "JAKARTA",
      "tgllahir": "15/04/1985"
    }
  ];

  // ── Fetch Data Pasien Backend /show_pasien ──
  const fetchPasienData = async (medrecVal = undefined, namaVal = undefined) => {
    setIsLoading(true);
    setFetchError(null);
    const apiUrl = getApiUrl();
    const cleanMedrec = (medrecVal !== undefined ? medrecVal : inputNoMedrec).trim();
    const cleanNama   = (namaVal !== undefined ? namaVal : inputNama).trim();

    try {
      let res;
      try {
        res = await fetch(`${apiUrl}/show_pasien`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            no_medrec: cleanMedrec,
            nama: cleanNama
          })
        });
      } catch {
        res = await fetch(`${apiUrl}/show_pasien?no_medrec=${encodeURIComponent(cleanMedrec)}&nama=${encodeURIComponent(cleanNama)}`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        });
      }

      if (!res.ok) throw new Error(`HTTP error ${res.status}`);
      const json = await res.json();

      let dataArray = [];
      if (Array.isArray(json)) {
        dataArray = json;
      } else if (json?.list_pasien && Array.isArray(json.list_pasien)) {
        dataArray = json.list_pasien;
      } else {
        dataArray = mockPasienData;
      }

      setPasienList(dataArray);
    } catch (err) {
      console.warn('Gagal fetch /show_pasien:', err);
      setPasienList(mockPasienData);
      setFetchError('Menggunakan data lokal. Gagal terhubung ke backend server.');
    } finally {
      setIsLoading(false);
    }
  };

  // Clock Effect & Initial Fetch
  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      setTime({
        date: now.toLocaleDateString('id-ID', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }),
        clock: now.toLocaleTimeString('id-ID') + ' WIB',
      });
    };
    updateTime();
    const interval = setInterval(updateTime, 1000);
    fetchPasienData();
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filter Data Pasien Lokal
  const filteredData = pasienList.filter(item => {
    const qMedrec = inputNoMedrec.toLowerCase().trim();
    const qNama   = inputNama.toLowerCase().trim();

    const matchMedrec = !qMedrec || (item.no_medrec || '').toLowerCase().includes(qMedrec);
    const matchNama   = !qNama || (item.nama || '').toLowerCase().includes(qNama);

    let matchGender = true;
    if (filterGender) {
      const kel = (item.kelamin || '').toLowerCase();
      if (filterGender === 'Laki-Laki') {
        matchGender = kel.includes('laki');
      } else if (filterGender === 'Perempuan') {
        matchGender = kel.includes('perempuan') || kel.includes('wanita');
      }
    }

    return matchMedrec && matchNama && matchGender;
  });

  // Handler Submit Search ke Database
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchPasienData(inputNoMedrec, inputNama);
  };

  const handleResetSearch = () => {
    setInputNoMedrec('');
    setInputNama('');
    setFilterGender('');
    setCurrentPage(1);
    fetchPasienData('', '');
  };

  // Pagination Logic
  const indexOfLast  = currentPage * itemsPerPage;
  const indexOfFirst = indexOfLast - itemsPerPage;
  const currentItems = filteredData.slice(indexOfFirst, indexOfLast);
  const totalPages   = Math.ceil(filteredData.length / itemsPerPage);

  // Handler Detail Modal
  const handleOpenDetailModal = (item) => {
    setSelectedPasien(item);
    setShowDetailModal(true);
  };

  // ── Handler Cetak Kartu Pasien (buat_kartu_pasien) ──
  const handleCetakKartuPasien = async (item) => {
    setSelectedPasien(item);
    setIsGeneratingKartu(true);
    setKartuError(null);
    setSelectedKartuData(null);
    setShowKartuModal(true);

    const apiUrl = getApiUrl();
    const noMedrec = item.no_medrec;

    try {
      let res;
      try {
        res = await fetch(`${apiUrl}/buat_kartu_pasien`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ no_medrec: noMedrec })
        });
      } catch {
        res = await fetch(`${apiUrl}/buat_kartu_pasien?no_medrec=${encodeURIComponent(noMedrec)}`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        });
      }

      if (!res.ok) throw new Error(`HTTP error ${res.status}`);
      const json = await res.json();

      if (json?.status === 1 || json?.data) {
        setSelectedKartuData(json.data || json);
      } else {
        throw new Error(json?.msg || 'Gagal membuat kartu pasien');
      }
    } catch (err) {
      console.warn('Gagal fetch /buat_kartu_pasien:', err);
      // Demo Fallback Kartu Data jika backend belum aktif
      const mr = item.no_medrec || '108112';
      setSelectedKartuData({
        no_medrec: mr,
        medrec1: mr.length >= 2 ? mr.substring(0, 2) : '10',
        medrec2: mr.length >= 4 ? mr.substring(2, 4) : '81',
        medrec3: mr.length >= 6 ? mr.substring(4, 6) : '12',
        nama_lengkap: item.nama,
        nmpasien1: item.nama ? item.nama.split(' ')[0] : 'PASIEN',
        nmpasien2: item.nama ? item.nama.split(' ')[1] || '' : '',
        nmpasien3: item.nama ? item.nama.split(' ')[2] || '' : '',
        tmp_lahir: item.tempat || 'BOGOR',
        tgl_lahir: item.tgllahir || '2009',
        preview_url: `/buat_kartupasien/${mr}?view=1`,
        download_url: `/buat_kartupasien/${mr}?download=1`,
        pdf_url: `/buat_kartupasien/${mr}?view=1`
      });
    } finally {
      setIsGeneratingKartu(false);
    }
  };

  const handlePrintIframe = () => {
    const iframe = document.getElementById('kartu-pdf-iframe');
    if (iframe && iframe.contentWindow) {
      try {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
        return;
      } catch (e) {
        // Fallback jika iframe di-block cross origin
      }
    }
    window.print();
  };

  return (
    <div style={styles.appContainer}>
      {/* ══ NAVBAR COMPACT HEADER ══ */}
      <nav style={styles.navbarCustom} className="d-flex align-items-center justify-content-between text-white shadow-sm">
        <div className="d-flex align-items-center gap-2">
          <span className="fw-bold fs-6 tracking-wide d-flex align-items-center gap-2">
            <i className="bi bi-people-fill text-warning fs-5"></i>
            SIMRS — Master Data Rekam Medis Pasien
          </span>
        </div>
        <div className="d-flex align-items-center gap-3">
          <span className="text-light" style={{ fontSize: '0.72rem', opacity: 0.9 }}>
            <i className="bi bi-calendar-event me-1"></i>{time.date} |
            <i className="bi bi-clock me-1 ms-1"></i>{time.clock}
          </span>
          <Button
            variant="warning"
            size="sm"
            className="fw-semibold text-dark shadow-sm"
            style={{ padding: '0.2rem 0.75rem', fontSize: '0.75rem', borderRadius: '6px' }}
            onClick={() => navigate('/home')}
          >
            <i className="bi bi-house-door-fill me-1"></i> Kembali ke Home
          </Button>
        </div>
      </nav>

      {/* ══ MAIN CONTENT ══ */}
      <div style={styles.mainContent}>

        {/* ── Stats Summary Bar ── */}
        <Row className="g-2 mb-2">
          <Col md={3} sm={6}>
            <div style={{ background: '#f0fdfa', border: '1.5px solid #0d948822', borderRadius: '10px', padding: '0.55rem 0.8rem', display: 'flex', alignItems: 'center', gap: '0.65rem' }} className="shadow-sm">
              <div style={{ background: '#0d9488', borderRadius: '8px', width: '38px', height: '38px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <i className="bi bi-person-lines-fill text-white fs-5"></i>
              </div>
              <div>
                <div className="fw-bold" style={{ fontSize: '1.1rem', color: '#0d9488', lineHeight: 1.1 }}>{pasienList.length}</div>
                <div className="text-muted" style={{ fontSize: '0.68rem' }}>Total Pasien Terdaftar</div>
              </div>
            </div>
          </Col>

          <Col md={3} sm={6}>
            <div style={{ background: '#eff6ff', border: '1.5px solid #2563eb22', borderRadius: '10px', padding: '0.55rem 0.8rem', display: 'flex', alignItems: 'center', gap: '0.65rem' }} className="shadow-sm">
              <div style={{ background: '#2563eb', borderRadius: '8px', width: '38px', height: '38px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <i className="bi bi-gender-male text-white fs-5"></i>
              </div>
              <div>
                <div className="fw-bold" style={{ fontSize: '1.1rem', color: '#2563eb', lineHeight: 1.1 }}>
                  {pasienList.filter(p => (p.kelamin || '').toLowerCase().includes('laki')).length}
                </div>
                <div className="text-muted" style={{ fontSize: '0.68rem' }}>Pasien Laki-Laki</div>
              </div>
            </div>
          </Col>

          <Col md={3} sm={6}>
            <div style={{ background: '#fdf2f8', border: '1.5px solid #db277722', borderRadius: '10px', padding: '0.55rem 0.8rem', display: 'flex', alignItems: 'center', gap: '0.65rem' }} className="shadow-sm">
              <div style={{ background: '#db2777', borderRadius: '8px', width: '38px', height: '38px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <i className="bi bi-gender-female text-white fs-5"></i>
              </div>
              <div>
                <div className="fw-bold" style={{ fontSize: '1.1rem', color: '#db2777', lineHeight: 1.1 }}>
                  {pasienList.filter(p => (p.kelamin || '').toLowerCase().includes('perempuan') || (p.kelamin || '').toLowerCase().includes('wanita')).length}
                </div>
                <div className="text-muted" style={{ fontSize: '0.68rem' }}>Pasien Perempuan</div>
              </div>
            </div>
          </Col>

          <Col md={3} sm={6}>
            <div style={{ background: '#f0fdf4', border: '1.5px solid #16a34a22', borderRadius: '10px', padding: '0.55rem 0.8rem', display: 'flex', alignItems: 'center', gap: '0.65rem' }} className="shadow-sm">
              <div style={{ background: '#16a34a', borderRadius: '8px', width: '38px', height: '38px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <i className="bi bi-card-heading text-white fs-5"></i>
              </div>
              <div>
                <div className="fw-bold" style={{ fontSize: '1.1rem', color: '#16a34a', lineHeight: 1.1 }}>
                  {pasienList.filter(p => p.no_ktp && p.no_ktp !== '-' && p.no_ktp.length > 5).length}
                </div>
                <div className="text-muted" style={{ fontSize: '0.68rem' }}>Memiliki NIK KTP Valid</div>
              </div>
            </div>
          </Col>
        </Row>

        {/* ── Main Card ── */}
        <Card style={styles.mainCard} className="shadow-sm border-0">
          {/* Card Header */}
          <div style={{ ...styles.cardHeader, ...styles.bgMenuHeader }}
            className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <span className="fw-bold">
              <i className="bi bi-person-vcard-fill me-1 text-warning"></i>
              Daftar Master Rekam Medis Pasien (Endpoint: /show_pasien)
            </span>
            <div className="d-flex gap-2 align-items-center">
              <Button
                variant="outline-light"
                size="sm"
                onClick={() => fetchPasienData(inputNoMedrec, inputNama)}
                disabled={isLoading}
                style={{ fontSize: '0.73rem', borderRadius: '6px' }}
              >
                {isLoading ? <Spinner size="sm" animation="border" /> : <i className="bi bi-arrow-clockwise"></i>} Refresh Data
              </Button>
            </div>
          </div>

          <Card.Body style={{ padding: '0.6rem' }}>
            {/* ── Form Card Pencarian Pasien ke Database (/show_pasien) ── */}
            <Card className="border-0 shadow-sm mb-2" style={{ background: '#f8fafc', borderLeft: '4px solid #0d9488', borderRadius: '10px' }}>
              <Card.Body className="p-2">
                <Form onSubmit={handleSearchSubmit}>
                  <Row className="g-2 align-items-center">
                    {/* Input 1: No. Medrec */}
                    <Col md={3} sm={6}>
                      <Form.Group className="d-flex align-items-center gap-1">
                        <Form.Label className="mb-0 fw-semibold text-dark" style={{ fontSize: '0.73rem', whiteSpace: 'nowrap' }}>
                          No. Medrec:
                        </Form.Label>
                        <Form.Control
                          type="text"
                          size="sm"
                          placeholder="Isikan No. Medrec (misal: 339727)"
                          value={inputNoMedrec}
                          onChange={e => setInputNoMedrec(e.target.value)}
                          style={{ fontSize: '0.78rem', fontFamily: 'monospace' }}
                        />
                      </Form.Group>
                    </Col>

                    {/* Input 2: Nama Pasien */}
                    <Col md={4} sm={6}>
                      <Form.Group className="d-flex align-items-center gap-1">
                        <Form.Label className="mb-0 fw-semibold text-dark" style={{ fontSize: '0.73rem', whiteSpace: 'nowrap' }}>
                          Nama Pasien:
                        </Form.Label>
                        <Form.Control
                          type="text"
                          size="sm"
                          placeholder="Isikan Nama Pasien (misal: SALIM)"
                          value={inputNama}
                          onChange={e => setInputNama(e.target.value)}
                          style={{ fontSize: '0.78rem' }}
                        />
                      </Form.Group>
                    </Col>

                    {/* Filter Gender */}
                    <Col md={2} sm={6}>
                      <Form.Select
                        size="sm"
                        value={filterGender}
                        onChange={e => { setFilterGender(e.target.value); setCurrentPage(1); }}
                        style={{ fontSize: '0.78rem' }}
                      >
                        <option value="">Semua Gender</option>
                        <option value="Laki-Laki">Laki-Laki</option>
                        <option value="Perempuan">Perempuan</option>
                      </Form.Select>
                    </Col>

                    {/* Tombol Eksekusi Cari Ke Database Server */}
                    <Col md={3} sm={6} className="d-flex gap-1">
                      <Button
                        type="submit"
                        variant="teal"
                        size="sm"
                        className="w-100 fw-bold d-flex align-items-center justify-content-center gap-1 text-white shadow-sm"
                        style={{ background: 'linear-gradient(135deg, #0d9488 0%, #0f766e 100%)', borderColor: '#0d9488', fontSize: '0.74rem', borderRadius: '6px' }}
                        disabled={isLoading}
                      >
                        {isLoading ? <Spinner size="sm" animation="border" /> : <i className="bi bi-search"></i>}
                        <span>Cari ke Database</span>
                      </Button>
                      <Button
                        type="button"
                        variant="outline-secondary"
                        size="sm"
                        style={{ fontSize: '0.73rem', borderRadius: '6px' }}
                        onClick={handleResetSearch}
                        title="Reset Isian Pencarian"
                      >
                        Reset
                      </Button>
                    </Col>
                  </Row>
                </Form>
              </Card.Body>
            </Card>

            {fetchError && (
              <div className="alert alert-warning py-1 px-3 mb-2" style={{ fontSize: '0.75rem' }}>
                <i className="bi bi-exclamation-triangle-fill me-1"></i>{fetchError}
              </div>
            )}

            {/* ── Data Table ── */}
            <div className="table-responsive" style={{ maxHeight: 'calc(100vh - 285px)', overflowY: 'auto' }}>
              <Table responsive hover striped bordered size="sm" className="align-middle mb-0" style={{ fontSize: '0.75rem' }}>
                <thead style={{ background: '#0f172a', color: '#fff', position: 'sticky', top: 0, zIndex: 10 }}>
                  <tr>
                    <th className="text-center" style={{ width: '35px' }}>#</th>
                    <th className="text-center" style={{ width: '95px' }}>No. Medrec</th>
                    <th className="text-center" style={{ width: '135px' }}>No. KTP (NIK)</th>
                    <th>Nama Pasien & Panggilan</th>
                    <th className="text-center" style={{ width: '100px' }}>Jenis Kelamin</th>
                    <th style={{ width: '130px' }}>Tempat, Tgl Lahir</th>
                    <th>Alamat Domisili Pasien</th>
                    <th>Penjamin / Perusahaan</th>
                    <th>Agama & Pekerjaan</th>
                    <th className="text-center" style={{ width: '70px' }}>Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td colSpan="10" className="text-center py-4">
                        <Spinner animation="border" variant="teal" size="sm" className="me-2" />
                        <span>Memuat master data rekam medis pasien...</span>
                      </td>
                    </tr>
                  ) : currentItems.length > 0 ? (
                    currentItems.map((item, idx) => (
                      <tr key={idx}>
                        <td className="text-center text-muted">{indexOfFirst + idx + 1}</td>

                        {/* No Medrec */}
                        <td className="text-center align-middle">
                          <span className="fw-bold text-teal font-monospace" style={{ color: '#0d9488', fontSize: '0.75rem' }}>
                            {item.no_medrec}
                          </span>
                        </td>

                        {/* No KTP */}
                        <td className="text-center font-monospace text-dark" style={{ fontSize: '0.72rem' }}>
                          {item.no_ktp && item.no_ktp !== '-' ? (
                            <span>{item.no_ktp}</span>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>

                        {/* Nama Pasien & Panggilan */}
                        <td>
                          <div className="fw-bold text-dark" style={{ fontSize: '0.76rem' }}>{item.nama}</div>
                          {item.nama_panggil && (
                            <div className="text-muted" style={{ fontSize: '0.67rem' }}>
                              Panggilan: <strong className="text-primary">{item.nama_panggil}</strong>
                            </div>
                          )}
                        </td>

                        {/* Jenis Kelamin */}
                        <td className="text-center align-middle">
                          <span className="fw-bold" style={{ fontSize: '0.7rem', color: (item.kelamin || '').toLowerCase().includes('perempuan') || (item.kelamin || '').toLowerCase().includes('wanita') ? '#ec4899' : '#3b82f6' }}>
                            {item.kelamin}
                          </span>
                        </td>

                        {/* Tempat, Tgl Lahir */}
                        <td style={{ fontSize: '0.73rem' }}>
                          <div className="fw-semibold text-dark">{item.tempat || '-'}</div>
                          <div className="text-muted" style={{ fontSize: '0.67rem' }}>{item.tgllahir || '-'}</div>
                        </td>

                        {/* Alamat Domisili */}
                        <td style={{ fontSize: '0.72rem' }}>
                          <div className="fw-semibold text-dark">{item.jln1 || '-'}</div>
                          <div className="text-muted" style={{ fontSize: '0.67rem' }}>
                            {item.komplek1 && item.komplek1 !== '-' ? `${item.komplek1}, ` : ''}
                            RT {item.rt || '00'}/RW {item.rw || '00'}, Kec. {item.kecamatan || '-'}, {item.kota || '-'}
                          </div>
                        </td>

                        {/* Penjamin / Perusahaan */}
                        <td className="align-middle">
                          <span className="fw-bold" style={{ fontSize: '0.68rem', color: (item.perusahaan || '').toUpperCase().includes('BPJS') ? '#10b981' : '#f59e0b' }}>
                            {item.perusahaan || 'UMUM'}
                          </span>
                        </td>

                        {/* Agama & Pekerjaan */}
                        <td style={{ fontSize: '0.72rem' }}>
                          <div className="fw-semibold text-dark">{item.agama || '-'}</div>
                          <div className="text-muted" style={{ fontSize: '0.67rem' }}>{item.pekerjaan || '-'}</div>
                        </td>

                        {/* Tombol Aksi */}
                        <td className="text-center">
                          <div className="d-flex justify-content-center gap-1">
                            <OverlayTrigger placement="top" overlay={<Tooltip>Detail Rincian Pasien</Tooltip>}>
                              <Button
                                size="sm"
                                variant="outline-info"
                                style={{ padding: '0.15rem 0.45rem', fontSize: '0.7rem', borderRadius: '5px' }}
                                onClick={() => handleOpenDetailModal(item)}
                              >
                                <i className="bi bi-eye-fill"></i>
                              </Button>
                            </OverlayTrigger>

                            <OverlayTrigger placement="top" overlay={<Tooltip>Cetak Kartu Pasien</Tooltip>}>
                              <Button
                                size="sm"
                                variant="outline-success"
                                style={{ padding: '0.15rem 0.45rem', fontSize: '0.7rem', borderRadius: '5px' }}
                                onClick={() => handleCetakKartuPasien(item)}
                              >
                                <i className="bi bi-card-heading"></i>
                              </Button>
                            </OverlayTrigger>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="10" className="text-center py-4 text-muted">
                        Tidak ada master data pasien yang ditemukan.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>

            {/* ── Pagination ── */}
            {totalPages > 1 && (
              <div className="d-flex justify-content-center mt-3">
                <Pagination size="sm" className="mb-0">
                  <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
                  <Pagination.Prev onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} disabled={currentPage === 1} />
                  {[...Array(Math.min(totalPages, 10))].map((_, i) => (
                    <Pagination.Item key={i + 1} active={i + 1 === currentPage} onClick={() => setCurrentPage(i + 1)}>
                      {i + 1}
                    </Pagination.Item>
                  ))}
                  {totalPages > 10 && <Pagination.Ellipsis disabled />}
                  <Pagination.Next onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages} />
                  <Pagination.Last onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} />
                </Pagination>
              </div>
            )}

            {/* ── Footer Info ── */}
            <div className="d-flex justify-content-between mt-3 pt-2 border-top text-muted" style={{ fontSize: '0.72rem' }}>
              <span>
                Menampilkan{' '}
                <strong>{filteredData.length > 0 ? indexOfFirst + 1 : 0}–{Math.min(indexOfLast, filteredData.length)}</strong>{' '}
                dari <strong>{filteredData.length}</strong> data pasien (Total: {pasienList.length})
              </span>
              <span>IP Server: <strong>{getApiUrl()}</strong></span>
            </div>
          </Card.Body>
        </Card>
      </div>

      {/* ══ MODAL DETAIL MASTER PASIEN ══ */}
      <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} centered size="lg">
        <Modal.Header closeButton style={{ background: 'linear-gradient(135deg, #0f172a 0%, #0d9488 100%)', color: '#fff' }}>
          <Modal.Title style={{ fontSize: '0.9rem', fontWeight: '700' }} className="d-flex align-items-center gap-2">
            <i className="bi bi-person-vcard text-warning fs-5"></i>
            <span>Detail Identitas Rekam Medis Pasien</span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-3 bg-light">
          {selectedPasien && (
            <Card className="border-0 shadow-sm mb-3">
              <Card.Body className="p-3" style={{ fontSize: '0.78rem' }}>
                <Row className="g-2">
                  <Col md={6} className="border-bottom pb-2 mb-2">
                    <div className="text-muted" style={{ fontSize: '0.68rem' }}>Nomor Rekam Medis (No. MR)</div>
                    <Badge bg="teal" style={{ background: '#0d9488', fontSize: '0.82rem' }} className="font-monospace">{selectedPasien.no_medrec}</Badge>
                    <div className="text-muted mt-1" style={{ fontSize: '0.7rem' }}>NIK KTP: <strong className="font-monospace text-dark">{selectedPasien.no_ktp || '-'}</strong></div>
                  </Col>

                  <Col md={6} className="border-bottom pb-2 mb-2">
                    <div className="text-muted" style={{ fontSize: '0.68rem' }}>Penjamin / Perusahaan</div>
                    <Badge bg="success" className="fs-6">{selectedPasien.perusahaan || 'UMUM'}</Badge>
                    <div className="text-muted mt-1" style={{ fontSize: '0.7rem' }}>Status Perkawinan: <strong>{selectedPasien.sts === 'K' ? 'Kawin (K)' : selectedPasien.sts === 'T' ? 'Belum Kawin (T)' : selectedPasien.sts}</strong></div>
                  </Col>

                  <Col md={12} className="bg-white p-2 rounded border mb-2">
                    <div className="text-muted" style={{ fontSize: '0.68rem' }}>Nama Lengkap Pasien</div>
                    <div className="fw-bold text-dark fs-5">{selectedPasien.nama}</div>
                    {selectedPasien.nama_panggil && (
                      <div className="text-muted" style={{ fontSize: '0.7rem' }}>Nama Panggilan: <strong className="text-primary">{selectedPasien.nama_panggil}</strong></div>
                    )}
                  </Col>

                  <Col md={6}>
                    <div className="text-muted" style={{ fontSize: '0.68rem' }}>Jenis Kelamin</div>
                    <Badge bg={(selectedPasien.kelamin || '').toLowerCase().includes('perempuan') ? 'danger' : 'primary'} className="fs-6">
                      {selectedPasien.kelamin}
                    </Badge>
                  </Col>
                  <Col md={6}>
                    <div className="text-muted" style={{ fontSize: '0.68rem' }}>Tempat & Tanggal Lahir</div>
                    <div className="fw-bold text-dark">{selectedPasien.tempat || '-'}, {selectedPasien.tgllahir || '-'}</div>
                  </Col>

                  <Col md={12} className="mt-2 pt-2 border-top">
                    <div className="text-muted" style={{ fontSize: '0.68rem' }}>Alamat Lengkap Domisili</div>
                    <div className="fw-semibold text-dark fs-6">{selectedPasien.jln1 || '-'}</div>
                    <div className="text-muted" style={{ fontSize: '0.72rem' }}>
                      Komplek: <strong>{selectedPasien.komplek1 || '-'}</strong> | RT: <strong>{selectedPasien.rt || '00'}</strong> / RW: <strong>{selectedPasien.rw || '00'}</strong>
                    </div>
                    <div className="text-muted" style={{ fontSize: '0.72rem' }}>
                      Kecamatan: <strong>{selectedPasien.kecamatan || '-'}</strong> | Kota/Kabupaten: <strong>{selectedPasien.kota || '-'}</strong>
                    </div>
                  </Col>

                  <Col md={6} className="mt-2 pt-2 border-top">
                    <div className="text-muted" style={{ fontSize: '0.68rem' }}>Agama</div>
                    <div className="fw-semibold text-dark">{selectedPasien.agama || '-'}</div>
                  </Col>
                  <Col md={6} className="mt-2 pt-2 border-top">
                    <div className="text-muted" style={{ fontSize: '0.68rem' }}>Pekerjaan / Kode</div>
                    <div className="fw-semibold text-dark">{selectedPasien.pekerjaan || '-'}</div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          )}
          <div className="d-flex justify-content-end border-top pt-2">
            <Button variant="secondary" size="sm" onClick={() => setShowDetailModal(false)}>
              Tutup
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      {/* ══ MODAL PREVIEW & CETAK KARTU PASIEN (buat_kartu_pasien) ══ */}
      <Modal show={showKartuModal} onHide={() => setShowKartuModal(false)} centered size="lg">
        <Modal.Header closeButton style={{ background: 'linear-gradient(135deg, #0d9488 0%, #0f766e 100%)', color: '#fff' }}>
          <Modal.Title style={{ fontSize: '0.92rem', fontWeight: '700' }} className="d-flex align-items-center gap-2">
            <i className="bi bi-card-heading text-warning fs-5"></i>
            <span>Preview & Cetak Kartu Berobat Pasien</span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-3 bg-light">
          {isGeneratingKartu ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="teal" className="mb-2" />
              <div className="fw-bold text-dark" style={{ fontSize: '0.88rem' }}>
                Sedang membuat dokumen Kartu Pasien via backend <code>/buat_kartu_pasien</code>...
              </div>
            </div>
          ) : selectedKartuData ? (
            <div>
              {/* Desain Visual Kartu Pasien (Digital ID Card Preview) */}
              <Card className="border-0 shadow-sm mb-3 overflow-hidden" style={{ borderRadius: '14px', background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 60%, #0f766e 100%)', color: '#ffffff' }}>
                <Card.Body className="p-3">
                  <div className="d-flex justify-content-between align-items-center border-bottom border-light border-opacity-25 pb-2 mb-2">
                    <div className="d-flex align-items-center gap-2">
                      <i className="bi bi-hospital text-warning fs-4"></i>
                      <div>
                        <div className="fw-bold text-white lh-1" style={{ fontSize: '0.9rem', letterSpacing: '0.5px' }}>
                          KARTU BEROBAT PASIEN
                        </div>
                        <div className="text-white-50" style={{ fontSize: '0.65rem' }}>RUMAH SAKIT UTAMA SIMRS</div>
                      </div>
                    </div>
                    <Badge bg="warning" text="dark" className="font-monospace" style={{ fontSize: '0.7rem' }}>
                      MASTER REKAM MEDIS
                    </Badge>
                  </div>

                  <Row className="align-items-center g-2">
                    <Col md={8}>
                      <div className="text-white-50" style={{ fontSize: '0.65rem' }}>NAMA LENGKAP PASIEN</div>
                      <div className="fw-bold text-warning text-uppercase mb-2" style={{ fontSize: '1.1rem', letterSpacing: '0.5px' }}>
                        {selectedKartuData.nama_lengkap || selectedPasien?.nama}
                      </div>

                      <Row className="g-2">
                        <Col xs={6}>
                          <div className="text-white-50" style={{ fontSize: '0.63rem' }}>TEMPAT, TGL LAHIR</div>
                          <div className="fw-semibold text-white" style={{ fontSize: '0.75rem' }}>
                            {selectedKartuData.tmp_lahir || '-'}, {selectedKartuData.tgl_lahir || '-'}
                          </div>
                        </Col>
                        <Col xs={6}>
                          <div className="text-white-50" style={{ fontSize: '0.63rem' }}>NOMOR REKAM MEDIS</div>
                          <div className="fw-bold font-monospace text-teal" style={{ fontSize: '0.95rem', color: '#2dd4bf' }}>
                            {selectedKartuData.no_medrec}
                          </div>
                        </Col>
                      </Row>
                    </Col>

                    <Col md={4} className="text-center border-start border-light border-opacity-25 ps-3">
                      <div className="p-2 bg-white rounded-3 shadow-sm text-dark d-inline-block">
                        <div className="text-muted mb-1" style={{ fontSize: '0.6rem' }}>DIGIT REKAM MEDIS</div>
                        <div className="d-flex justify-content-center gap-1 font-monospace fw-bold" style={{ fontSize: '1.2rem', color: '#0f766e' }}>
                          <span className="border px-1 rounded bg-light">{selectedKartuData.medrec1 || '10'}</span>
                          <span>-</span>
                          <span className="border px-1 rounded bg-light">{selectedKartuData.medrec2 || '81'}</span>
                          <span>-</span>
                          <span className="border px-1 rounded bg-light">{selectedKartuData.medrec3 || '12'}</span>
                        </div>
                        <i className="bi bi-qr-code-scan display-6 mt-1 text-secondary d-block"></i>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              {/* PDF Preview Iframe jika URL tersedia */}
              {selectedKartuData.preview_url || selectedKartuData.pdf_url ? (
                <div className="mb-3 rounded overflow-hidden border shadow-sm" style={{ background: '#fff' }}>
                  <div className="bg-dark text-white px-3 py-1.5 d-flex justify-content-between align-items-center" style={{ fontSize: '0.72rem' }}>
                    <span><i className="bi bi-file-earmark-pdf me-1 text-danger"></i> Document PDF Preview Kartu Pasien</span>
                    <span className="font-monospace text-white-50">{selectedKartuData.preview_url || selectedKartuData.pdf_url}</span>
                  </div>
                  <iframe
                    id="kartu-pdf-iframe"
                    title="Preview Kartu Pasien PDF"
                    src={`${getApiUrl()}${selectedKartuData.preview_url || selectedKartuData.pdf_url}`}
                    style={{ width: '100%', height: '320px', border: 'none' }}
                  />
                </div>
              ) : null}

              {kartuError && (
                <div className="alert alert-warning py-1 px-3 mb-2" style={{ fontSize: '0.75rem' }}>
                  <i className="bi bi-exclamation-triangle-fill me-1"></i>{kartuError}
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-4 text-muted">
              Gagal memuat preview kartu pasien.
            </div>
          )}

          <div className="d-flex justify-content-between align-items-center border-top pt-2 mt-2">
            <div>
              {selectedKartuData?.download_url && (
                <a
                  href={`${getApiUrl()}${selectedKartuData.download_url}`}
                  target="_blank"
                  rel="noreferrer"
                  className="btn btn-outline-primary btn-sm fw-bold d-inline-flex align-items-center gap-1"
                  style={{ fontSize: '0.75rem', borderRadius: '6px' }}
                >
                  <i className="bi bi-download"></i>
                  <span>Download File PDF</span>
                </a>
              )}
            </div>

            <div className="d-flex gap-2">
              <Button
                variant="success"
                size="sm"
                className="fw-bold d-flex align-items-center gap-1.5 shadow-sm text-white"
                style={{ fontSize: '0.78rem', borderRadius: '6px', background: '#16a34a', borderColor: '#16a34a' }}
                onClick={handlePrintIframe}
                disabled={isGeneratingKartu || !selectedKartuData}
              >
                <i className="bi bi-printer-fill fs-6"></i>
                <span>Cetak ke Printer</span>
              </Button>
              <Button variant="secondary" size="sm" style={{ fontSize: '0.75rem', borderRadius: '6px' }} onClick={() => setShowKartuModal(false)}>
                Tutup
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

// ══════════════ STYLES ══════════════
const styles = {
  appContainer: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 40%, #e2e8f0 100%)',
    display: 'flex', flexDirection: 'column',
  },
  navbarCustom: {
    background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 60%, #0d9488 100%)',
    padding: '0.25rem 1rem', flexShrink: 0,
  },
  mainContent: {
    flex: 1, padding: '0.4rem', overflowY: 'auto',
  },
  mainCard: {
    borderRadius: '12px', border: '1.5px solid #cbd5e1',
    boxShadow: '0 8px 30px rgba(15, 23, 42, 0.06)',
    background: '#ffffff', overflow: 'hidden',
  },
  cardHeader: {
    padding: '0.4rem 0.75rem', color: '#ffffff', fontWeight: 600, fontSize: '0.83rem',
  },
  bgMenuHeader: {
    background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0d9488 100%)',
  },
};

export default DataPasien;
